import React, {Component} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import type {WrappedComponentProps} from 'react-intl';
import {withRouter} from 'react-router-dom';
import type {RouteComponentProps} from 'react-router-dom';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../constants';
import Util from '../../forecast-app/shared/util/util';
import InlineLoader from '../../forecast-app/shared/components/inline-loader/inline_loader';
import GenericModal from './generic_modal';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import ImportEconomicCustomersMutation from '../../mutations/ImportEconomicCustomersMutation';
import {Checkbox} from '@/web-components';
import {hideLoader, showLoader} from '../global_loader';
import DirectApi from '../../directApi';
import {finishClientImport, loadImportContacts, prepareImportBillTos} from './importClientsBillTo';
import type {BillFromRef, Contact} from '../../forecast-app/admin-tab/finance-page/InvoiceIntegrationsUtils';
import type {ImportEconomicCustomersMutation$data} from '../../mutations/__generated__/ImportEconomicCustomersMutation.graphql';

/** A customer from economic/unimported_customers. */
type EconomicCustomer = {id: number; name: string};

type Props = WrappedComponentProps &
	RouteComponentProps & {
		closeModal: () => void;
		billFroms: readonly BillFromRef[] | null;
	};

type State = {
	economicCustomers: EconomicCustomer[];
	fetchingCustomers: boolean;
	selectedCustomers: number[];
	selectAll?: boolean;
	importing?: boolean;
};

class EconomicImportCustomersModal extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = {
			economicCustomers: [],
			fetchingCustomers: true,
			selectedCustomers: [],
		};

		// Started now so it overlaps with the user picking customers; resolves [] with the flag off.
		this.importContacts = loadImportContacts(props.billFroms, {economicEnabled: true});

		this.getContacts();
	}

	/** The contacts behind the list, for the Bill To lookup; see loadImportContacts. */
	importContacts: Promise<Contact[]>;

	getContacts() {
		DirectApi.Fetch('economic/unimported_customers').then((contacts: {list: EconomicCustomer[]}) => {
			this.setState({
				economicCustomers: contacts.list.sort((a, b) =>
					a.name.toUpperCase().trim() > b.name.toUpperCase().trim() ? 1 : -1
				),
				fetchingCustomers: false,
			});
		});
	}

	toggleSelectAll() {
		if (this.state.selectAll) {
			this.setState({
				selectedCustomers: [],
				selectAll: false,
			});
		} else {
			this.setState({
				selectedCustomers: this.state.economicCustomers.map(customer => customer.id),
				selectAll: true,
			});
		}
	}

	toggleContact(economicCustomerId: number) {
		if (this.state.selectedCustomers.includes(economicCustomerId)) {
			const selectedCustomers = this.state.selectedCustomers.filter(id => id !== economicCustomerId);
			this.setState({
				selectedCustomers,
				selectAll: false,
			});
		} else {
			const selectedCustomers = this.state.selectedCustomers;
			selectedCustomers.push(economicCustomerId);
			this.setState({
				selectedCustomers,
			});
		}
	}

	async syncContacts() {
		this.setState({importing: true});
		showLoader();
		const {billTos, skippedCount} = await prepareImportBillTos(
			this.props.billFroms,
			'ECONOMIC',
			this.state.selectedCustomers,
			this.importContacts
		);
		Util.CommitMutation(
			ImportEconomicCustomersMutation,
			{
				economicCustomerIds: this.state.selectedCustomers,
				...(billTos ? {billTos} : {}),
			},
			(response: ImportEconomicCustomersMutation$data) => {
				this.setState({importing: false});
				this.props.closeModal();
				hideLoader();
				finishClientImport(
					skippedCount,
					response?.importEconomicCustomers?.unlinkedBillToCount,
					this.props.intl.formatMessage
				);
			}
		);
	}

	render() {
		const {formatMessage} = this.props.intl;
		const content = (
			<div className="integration-sync-clients-modal-content">
				<div className="integration-sync-clients-modal-description">
					<FormattedMessage id="integrations.economic.sync_clients.description" />
				</div>
				{this.state.fetchingCustomers ? (
					<InlineLoader />
				) : (
					<CustomScrollDiv autoHeight autoHeightMin={10} autoHeightMax={'60vh'}>
						<div className="integration-clients">
							{this.state.economicCustomers && this.state.economicCustomers.length > 0 ? (
								<>
									<div className="integration-client" key={'0'}>
										<Checkbox checked={this.state.selectAll} onClick={this.toggleSelectAll.bind(this)} />
										<FormattedMessage id="common.select_all" />
									</div>
									{this.state.economicCustomers.map(customer => (
										<div className="integration-client" key={customer.id}>
											<Checkbox
												checked={this.state.selectedCustomers.includes(customer.id)}
												onClick={this.toggleContact.bind(this, customer.id)}
											/>
											{customer.name}
										</div>
									))}
								</>
							) : (
								<FormattedMessage id="integrations.economic.all_contacts_synched" />
							)}
						</div>
					</CustomScrollDiv>
				)}
			</div>
		);

		return (
			<GenericModal
				headerText={<FormattedMessage id="integrations.economic.sync_clients" />}
				closeModal={this.props.closeModal}
				className="integration-sync-clients-modal"
				buttons={[
					{
						text: formatMessage({id: 'common.cancel'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.WHITE,
					},
					{
						text: formatMessage(
							{id: 'common.create_x_clients'},
							{numOfClients: this.state.selectedCustomers.length}
						),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.GREEN,
						callback: this.syncContacts.bind(this),
						disabled: this.state.importing || this.state.selectedCustomers.length === 0,
						defaultCallback: () => null,
						preventDefaultClose: true,
					},
				]}
				content={content}
			/>
		);
	}
}

export default injectIntl(withRouter(EconomicImportCustomersModal));
